/**
 * BuildingsListing — modern Buildings listing using FilterBar + Toolbar + Quimby Table.
 *
 * Replaces the legacy Kendo Grid / FilterPanel / SearchPanel / ColumnMenu shell for both
 * the standalone /buildings page and the account Buildings tab.
 *
 * Preserved behaviors:
 *   - All current column cells (name link with QB/invalid-address icons, account link).
 *   - FilterBar with all current filters + context-hidden and optional gate rules.
 *   - Persistent FilterBar Export CSV always exports the full filtered set
 *     (`all=true`) so a global export does not require a selection.
 *   - Floating toolbar Export CSV (visible only when rows are selected) sends
 *     `checked_ids` for a page subset; select-all-entirely keeps `all=true`.
 *   - Row checkboxes + header page-select + Table-owned Toolbar.Selection
 *     (IP-15865; same Table `selection.mode === 'entire'` path as Series).
 *   - Page sizes 25/50/100 with direct page-number entry.
 *   - Loading / empty / retryable error states.
 *   - Live usePublishListContextBridge publication.
 *   - Legacy localStorage preference migration via useBuildingsPreferences.
 *   - Column visibility with immediate checkbox updates and Reset (task-06/07).
 *
 * Standalone context (showHeader=true):
 *   - Renders WorkspaceHeader with title and header actions.
 *   - Primary: New Building. Secondary (QBO / QBD / sync tax rates) sit behind
 *     ListingHeaderMoreMenu (3-dot More), separated by a vertical rule.
 *   - Configure (ColumnVisibilityControl) on the Toolbar far right (not in WorkspaceHeader).
 *   - WorkspaceHeader divider={false}.
 *
 * Account embed context (showHeader=false, accountId provided):
 *   - No extra WorkspaceHeader. Account tab chrome stays as-is.
 *   - Account filter is hidden; scope is injected by the serializer (IP-15555).
 *
 * Hook adoption:
 *   - A1  useListingScopeFromProps
 *   - A2  useListingPreferenceBootstrap
 *   - A3  usePersistedListingPreferences
 *   - A5  useListingFilterBarControllers
 *   - A6  useDebouncedListingSearch
 *   - A7  useListingTableControllers
 *   - A10 useListingColumnVisibilityState
 *   - A11 useListingQueryViewModel
 *   - A15 usePublishListContextBridge
 *   Skip: A8/A9 (deprecated — Table owns selection), A12 (no inline edit),
 *         A13/A14 (no mass-action invalidation).
 */
import React, { useCallback, useEffect, useMemo } from 'react';
import PropTypes from 'prop-types';
import { Table, TableSize } from '@ui/molecules/Table/Table';
import { FilterBar } from '@ui/organisms/FilterBar';
import { WorkspaceHeader } from '@ui/molecules/Header/WorkspaceHeader/WorkspaceHeader';
import { ListingHeaderMoreMenu } from '@ui/molecules/Header/ListingHeaderMoreMenu';
import { Button } from '@ui/atoms/Button';
import { TenantContextProvider } from '@contexts/tenant-context';
import I18n, { translate } from '@utils/i18n';
import { hasGlobalTerritoriesApplied } from '@helpers/territories';
import { loadSelectedFromOptions } from '@helpers/lookupByIds';
import { useListingQueryState, useListingScopeFromProps, useListingPreferenceBootstrap, usePersistedListingPreferences, useListingFilterBarControllers, useDebouncedListingSearch, useListingTableControllers, useListingColumnVisibilityState, useListingQueryViewModel, usePublishListContextBridge } from '@hooks/listings';
import { INITIAL_BUILDING_FILTERS, DEFAULT_ACTIVE_FILTER_KEYS } from './types';
import { useBuildingsPreferences } from './hooks/useBuildingsPreferences';
import { useBuildingsQuery } from './hooks/useBuildingsQuery';
import { columnPolicyForScope } from './adapters/buildingsColumnPolicy';
import { serializeBuildingsNarrowing } from './adapters/buildingsStateSerializer';
import { createArrayLoader } from './adapters/createArrayLoader';
import { createAccountsLoader, createTagsLoader, createAdditionalTerritoriesLoader, createSalesPersonsLoader, createAccountsByIdsLoader, createTagsByIdsLoader, createAdditionalTerritoriesByIdsLoader, createSalesPersonsByIdsLoader } from './services/BuildingsLookupService';
import { useBuildingsFilterConfig } from './filters/useBuildingsFilterConfig';
import { buildVisibleColumns, buildColumnVisibilityItems } from './components/BuildingsColumns';
import BuildingsExportCsvModal from './nested/ExportCsvModal';
// ---------------------------------------------------------------------------
// Constants
// ---------------------------------------------------------------------------
const PAGE_SIZE_OPTIONS = [25, 50, 100];
const DEFAULT_SORT = { field: 'name', direction: 'asc' };
const NON_SORTABLE_KEYS = ['siteId', 'inspectionsCount'];
const NS = 'features.buildings.buildings_index';
// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------
export function BuildingsListing({ accountId = null, compliancePlatformsForSelect = [], territoriesForSelect = [], showHeader = false, pageTitle = '', newBuildingUrl = null, canCreateBuilding = false, newBuildingForbiddenMessage = null, qboEnabled = false, qbdEnabled = false, qboImportUrl = null, qbdImportUrl = null, qbdSyncTaxRatesUrl = null }) {
    var _a, _b, _c, _d, _e, _f, _g, _h, _j, _k, _l;
    // --- A1: Scope ---
    const scope = useListingScopeFromProps({ accountId });
    // --- Preferences ---
    const { preferences, savePreferences, clearPreferences } = useBuildingsPreferences({ scope });
    // --- Column policy (IP-15282: memoized in useBuildingsPreferences) ---
    const policy = useMemo(() => columnPolicyForScope(scope), [scope]);
    // --- A2: Bootstrap initial state values from preferences ---
    const { initialActiveFilterKeys, initialVisibleColumnKeys, initialPageSize, initialSort, initialFilters } = useListingPreferenceBootstrap({
        preferences,
        defaultActiveFilterKeys: [...DEFAULT_ACTIVE_FILTER_KEYS],
        columnPolicy: policy,
        defaultFilters: INITIAL_BUILDING_FILTERS,
        defaultSort: DEFAULT_SORT,
        defaultPageSize: 25,
        includeSearch: false
    });
    // --- Query state ---
    const { state, setFilters, setSearch, setSort, setPageNumber, setPageSize } = useListingQueryState({
        scope,
        page: { number: 1, size: initialPageSize },
        sort: initialSort,
        filters: initialFilters,
        search: ''
    });
    // --- Lookup loaders (memoized for identity stability — IP-15282) ---
    const loadAccounts = useMemo(() => createAccountsLoader(), []);
    const loadTags = useMemo(() => createTagsLoader(), []);
    const loadAdditionalTerritories = useMemo(() => createAdditionalTerritoriesLoader(), []);
    const loadSalesPersons = useMemo(() => createSalesPersonsLoader(), []);
    const loadTerritories = useMemo(() => createArrayLoader(territoriesForSelect), [territoriesForSelect]);
    const loadSelectedAccounts = useMemo(() => createAccountsByIdsLoader(), []);
    const loadSelectedTags = useMemo(() => createTagsByIdsLoader(), []);
    const loadSelectedAdditionalTerritories = useMemo(() => createAdditionalTerritoriesByIdsLoader(), []);
    const loadSelectedSalesPersons = useMemo(() => createSalesPersonsByIdsLoader(), []);
    const loadSelectedTerritories = useMemo(() => loadSelectedFromOptions(territoriesForSelect), [territoriesForSelect]);
    // --- Query + A11: View model ---
    const buildingsQuery = useBuildingsQuery(state);
    const { rows, totalCount, totalPages, isLoading, isError, refetch } = useListingQueryViewModel({
        query: buildingsQuery,
        pageSize: state.page.size,
        getTotalCount: (d) => d.total_count
    });
    // A5/A7 still accept an optional clearSelection. Table-owned selection resets
    // via selection.resetKey, so this stays a no-op.
    const clearSelection = useCallback(() => { }, []);
    // --- A5: Filter bar controllers ---
    const { activeFilterKeys, handleFiltersChange, handleFilterToggle, handleResetFilters, handleResetCache } = useListingFilterBarControllers({
        setFilters,
        initialFilters: INITIAL_BUILDING_FILTERS,
        initialActiveFilterKeys,
        clearSelection,
        clearPreferences
    });
    // Hide additionalTerritory when primary territory scope drops (global territories
    // off and territory filter empty). Must clear the hidden value so it is not still
    // applied to the query / CSV export. Matches legacy useFilters.jsx.
    useEffect(() => {
        const hasPrimaryTerritoryScope = hasGlobalTerritoriesApplied() || state.filters.territory.length > 0;
        if (!hasPrimaryTerritoryScope && state.filters.additionalTerritory.length > 0) {
            handleFiltersChange(Object.assign(Object.assign({}, state.filters), { additionalTerritory: [] }));
        }
    }, [state.filters, handleFiltersChange]);
    // --- A6: Debounced search (search is NOT persisted) ---
    const { searchInput, handleSearchChange } = useDebouncedListingSearch({ setSearch });
    // --- A7: Table navigation controllers ---
    const { handleSort, handlePageChange, handlePageSizeChange } = useListingTableControllers({
        setSort,
        setPageNumber,
        setPageSize,
        clearSelection
    });
    // --- A10: Column visibility state ---
    const buildVisibleColumnsCasted = useCallback((keys) => buildVisibleColumns(keys), []);
    const buildVisibilityItemsFn = useCallback((p) => buildColumnVisibilityItems(p.allowed, p.required), []);
    const { visibleColumnKeys, visibleColumns: visibleColumnsRaw, columnVisibilityItems, sortableColumns, handleColumnVisibilityApply } = useListingColumnVisibilityState({
        policy,
        initialVisibleColumnKeys,
        buildVisibleColumns: buildVisibleColumnsCasted,
        buildVisibilityItems: buildVisibilityItemsFn,
        nonSortableKeys: NON_SORTABLE_KEYS
    });
    const visibleColumns = visibleColumnsRaw;
    // --- A3: Persist preferences (search is excluded) ---
    usePersistedListingPreferences({
        savePreferences,
        pageSize: state.page.size,
        sort: state.sort,
        filters: state.filters,
        activeFilterKeys,
        visibleColumnKeys
    });
    // --- A15: Publish list context bridge ---
    usePublishListContextBridge({
        page: state.page,
        sort: state.sort,
        filters: state.filters,
        search: state.search,
        totalCount,
        rows
    });
    // --- Filter config (useBuildingsFilterConfig from task-04) ---
    const { filters: filterConfigs } = useBuildingsFilterConfig({
        scope,
        filters: state.filters,
        activeFilterKeys,
        onChange: handleFiltersChange,
        onToggle: handleFilterToggle,
        loadAccounts,
        loadTerritories,
        loadAdditionalTerritories,
        loadTags,
        loadSalesPersons,
        loadSelectedAccounts,
        loadSelectedTerritories,
        loadSelectedAdditionalTerritories,
        loadSelectedTags,
        loadSelectedSalesPersons,
        compliancePlatformsForSelect
    });
    // --- CSV export params (narrowing only: filters + search, no page/sort) ---
    const exportQueryParams = useMemo(() => serializeBuildingsNarrowing(state), [state]);
    // Include narrowing so filter/search clears selection even when page IDs match.
    const selectionResetKey = `${exportQueryParams.toString()}|${(_c = (_b = (_a = buildingsQuery.data) === null || _a === void 0 ? void 0 : _a.data) === null || _b === void 0 ? void 0 : _b.map((r) => r.id).join(',')) !== null && _c !== void 0 ? _c : ''}|${state.page.number}|${state.page.size}|${(_e = (_d = state.sort) === null || _d === void 0 ? void 0 : _d.field) !== null && _e !== void 0 ? _e : ''}|${(_g = (_f = state.sort) === null || _f === void 0 ? void 0 : _f.direction) !== null && _g !== void 0 ? _g : ''}`;
    // ---------------------------------------------------------------------------
    // Render helpers
    // ---------------------------------------------------------------------------
    const tableColSpan = visibleColumns.length + 1;
    const tableErrorContent = (React.createElement("tr", null,
        React.createElement("td", { colSpan: tableColSpan, className: "buildings-listing__error" },
            React.createElement("i", { className: "fa-light fa-triangle-exclamation" }),
            React.createElement("span", null, I18n.t('generic.error')),
            React.createElement("button", { type: "button", className: "qmb-control--sm", onClick: () => {
                    void refetch();
                } }, I18n.t('generic.retry')))));
    const tableEmptyContent = (React.createElement("tr", null,
        React.createElement("td", { colSpan: tableColSpan, className: "buildings-listing__empty" },
            React.createElement("span", null, I18n.t('generic.no_results')))));
    // --- Standalone header actions: primary visible; secondary behind More ---
    const secondaryHeaderItems = useMemo(() => {
        const items = [];
        if (qboEnabled && qboImportUrl) {
            items.push({ href: qboImportUrl, label: I18n.t('buildings.index.import.qbo') });
        }
        if (qbdEnabled && qbdImportUrl) {
            items.push({ href: qbdImportUrl, label: I18n.t('buildings.index.import.qbd') });
        }
        if (qbdEnabled && qbdSyncTaxRatesUrl) {
            items.push({
                href: qbdSyncTaxRatesUrl,
                label: I18n.t('buildings.index.sync_tax_rates'),
                method: 'post',
                confirm: I18n.t('buildings.index.sync_tax_rates_confirm')
            });
        }
        return items;
    }, [qboEnabled, qboImportUrl, qbdEnabled, qbdImportUrl, qbdSyncTaxRatesUrl]);
    const headerActions = useMemo(() => {
        if (!showHeader)
            return undefined;
        const hasSecondary = secondaryHeaderItems.length > 0;
        return (React.createElement(React.Fragment, null,
            newBuildingUrl &&
                (canCreateBuilding ? (React.createElement(Button, { variant: "primary", onClick: () => {
                        window.location.href = newBuildingUrl;
                    } },
                    React.createElement("i", { className: "fa-light fa-circle-plus" }),
                    ` ${I18n.t('buildings.kendo_index.new_building')}`)) : (
                // Decision 26: show disabled primary + tooltip when policy forbids creation.
                // Do not omit the button — absence would make the action unreachable.
                // newBuildingForbiddenMessage comes from Rails upgrade_plan helper (may be nil).
                React.createElement("span", { "data-toggle": "popover", "data-html": "true", "data-content": newBuildingForbiddenMessage !== null && newBuildingForbiddenMessage !== void 0 ? newBuildingForbiddenMessage : undefined, "data-placement": "bottom" },
                    React.createElement(Button, { variant: "primary", disabled: true },
                        React.createElement("i", { className: "fa-light fa-circle-plus" }),
                        ` ${I18n.t('buildings.kendo_index.new_building')}`)))),
            hasSecondary && (React.createElement(React.Fragment, null,
                React.createElement("hr", { className: "header__actions-split", "aria-hidden": "true", "data-testid": "listing-header-actions-split" }),
                React.createElement(ListingHeaderMoreMenu, { items: secondaryHeaderItems, label: I18n.t('generic.more') })))));
    }, [showHeader, newBuildingUrl, canCreateBuilding, newBuildingForbiddenMessage, secondaryHeaderItems]);
    // ---------------------------------------------------------------------------
    // Render
    // ---------------------------------------------------------------------------
    return (React.createElement(TenantContextProvider, null,
        showHeader && pageTitle && React.createElement(WorkspaceHeader, { divider: false, title: pageTitle, actions: headerActions }),
        React.createElement(Table, { data: rows, columns: visibleColumns, variant: "listing", size: TableSize.Full, selectable: true, getItemId: (row) => row.id, selection: {
                mode: 'entire',
                selectAllLabel: translate('table.selected_global_counter', {
                    namespace: NS,
                    count: totalCount
                }),
                resetKey: selectionResetKey
            }, sortable: true, sortableColumns: sortableColumns, onSort: handleSort, sortBy: (_j = (_h = state.sort) === null || _h === void 0 ? void 0 : _h.field) !== null && _j !== void 0 ? _j : null, sortDirection: ((_l = (_k = state.sort) === null || _k === void 0 ? void 0 : _k.direction) !== null && _l !== void 0 ? _l : null), onPageChange: handlePageChange, serverCurrentPage: state.page.number, serverTotalPages: totalPages, serverTotalItems: totalCount, pageSize: state.page.size, pageSizeOptions: PAGE_SIZE_OPTIONS, onPageSizeChange: handlePageSizeChange, isLoading: isLoading, isError: isError, errorContent: tableErrorContent, emptyContent: tableEmptyContent, enableCustomScrollbar: true, filterBar: React.createElement(FilterBar, { filters: filterConfigs, onResetFilters: handleResetFilters, onFilterToggle: handleFilterToggle, showSearch: true, searchValue: searchInput, onSearchChange: handleSearchChange, searchPlaceholder: I18n.t('filters.search'), allowResetCache: process.env.RAILS_ENV !== 'production', onResetCache: handleResetCache },
                React.createElement("div", { "data-testid": "buildings-export-csv-global" },
                    React.createElement(BuildingsExportCsvModal, { disabled: false, buildingIds: null, queryParams: exportQueryParams }))), columnVisibility: {
                items: columnVisibilityItems,
                visibleKeys: visibleColumnKeys,
                defaultKeys: policy.defaults,
                onApply: handleColumnVisibilityApply
            }, toolbar: {
                moreActionsLabel: I18n.t('generic.more'),
                groups: [
                    {
                        id: 'buildings-export',
                        content: (selection) => (React.createElement(BuildingsExportCsvModal, { disabled: false, buildingIds: selection.selectAllEntire || selection.selectedIds.length === 0
                                ? null
                                : selection.selectedIds.join(','), queryParams: exportQueryParams }))
                    }
                ]
            } })));
}
BuildingsListing.propTypes = {
    accountId: PropTypes.number,
    compliancePlatformsForSelect: PropTypes.array,
    territoriesForSelect: PropTypes.array,
    showHeader: PropTypes.bool,
    pageTitle: PropTypes.string,
    newBuildingUrl: PropTypes.string,
    canCreateBuilding: PropTypes.bool,
    newBuildingForbiddenMessage: PropTypes.string,
    qboEnabled: PropTypes.bool,
    qbdEnabled: PropTypes.bool,
    qboImportUrl: PropTypes.string,
    qbdImportUrl: PropTypes.string,
    qbdSyncTaxRatesUrl: PropTypes.string
};
